import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Sparkles as SparkIcon } from "lucide-react";
import SparkDialog from "@/components/SparkDialog";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Quote,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, Link2,
  Heading1, Heading2, Heading3, Pilcrow, RemoveFormatting, Type, Volume2, Pause, Square, Play, Wand2, Minus,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { correctToken, matchCase } from "@/lib/autocorrect";

const AC_KEY = "codex.autocorrect";

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#0ea5e9", "#ec4899"];
const FONTS = [
  { label: "Serif (Merriweather)", value: "Merriweather, Georgia, serif" },
  { label: "Sans (Jakarta)", value: "'Plus Jakarta Sans', sans-serif" },
  { label: "Mono (JetBrains)", value: "'JetBrains Mono', monospace" },
];
const SCALE_KEY = "codex.manuscriptScale";
const SCALES = [0.8, 0.9, 1, 1.1, 1.25, 1.4, 1.6, 1.8];
const BASE_REM = 1.1875;

// Keep pasted content clean: strip inline styles/classes and font/span wrappers so
// pasted text inherits the manuscript's own size, colour and zoom instead of carrying
// hardcoded sizes/colours from the source (which show up as odd sizes + broken exports).
const KEEP_TAGS = new Set(["P", "BR", "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "UL", "OL", "LI", "BLOCKQUOTE", "H1", "H2", "H3", "A", "HR"]);
function sanitizePastedHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 1) {
        walk(child);
        const tag = child.tagName;
        if (!KEEP_TAGS.has(tag)) {
          // Unwrap disallowed elements (span, font, div, etc.) but keep their text.
          const parent = child.parentNode;
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
        } else {
          const href = tag === "A" ? child.getAttribute("href") : null;
          Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));
          if (href) child.setAttribute("href", href);
        }
      }
    });
  };
  walk(doc.body);
  return doc.body.innerHTML.replace(/&nbsp;/gi, " ");
}
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function Btn({ onClick, title, active, children, testid }) {
  return (
    <button type="button" title={title} data-testid={testid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors shrink-0
        ${active ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-foreground"}`}>
      {children}
    </button>
  );
}
function Sep() { return <Separator orientation="vertical" className="h-6 mx-1" />; }

export default forwardRef(function RichEditor({ initialHtml, onChange, spellcheck, onStats, initialScroll = 0, protectedWords, customRules, projectId, chapterId, prevChapterId, aiTools = true }, apiRef) {
  const [sparkOpen, setSparkOpen] = useState(false);
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const savedRange = useRef(null);
  const [tick, setTick] = useState(0);
  const [autocorrect, setAutocorrect] = useState(() => localStorage.getItem(AC_KEY) !== "0");
  const correctingRef = useRef(false);
  useEffect(() => { localStorage.setItem(AC_KEY, autocorrect ? "1" : "0"); }, [autocorrect]);
  // Display-only zoom for the manuscript page (remembered globally, not saved into the doc).
  const [scale, setScale] = useState(() => {
    const v = parseFloat(localStorage.getItem(SCALE_KEY));
    return Number.isFinite(v) && v > 0 ? v : 1;
  });
  useEffect(() => { localStorage.setItem(SCALE_KEY, String(scale)); }, [scale]);
  const stepScale = (dir) => {
    let idx = SCALES.indexOf(scale);
    if (idx === -1) idx = SCALES.reduce((b, s, i) => (Math.abs(s - scale) < Math.abs(SCALES[b] - scale) ? i : b), 2);
    setScale(SCALES[Math.min(SCALES.length - 1, Math.max(0, idx + dir))]);
  };

  // --- Read aloud (built-in browser speech; free, private, no credits) ---
  const supportsTTS = typeof window !== "undefined" && "speechSynthesis" in window;
  const [reading, setReading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rate, setRate] = useState(() => parseFloat(localStorage.getItem("codex.ttsRate")) || 1);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("codex.ttsVoice") || "");
  const speechState = useRef({ sentences: [], idx: 0, stop: false });
  useEffect(() => { localStorage.setItem("codex.ttsRate", String(rate)); }, [rate]);
  useEffect(() => { if (voiceURI) localStorage.setItem("codex.ttsVoice", voiceURI); }, [voiceURI]);
  useEffect(() => {
    if (!supportsTTS) return undefined;
    const load = () => {
      const vs = window.speechSynthesis.getVoices();
      setVoices(vs);
      setVoiceURI((cur) => cur || (vs.find((v) => /en-GB/i.test(v.lang))?.voiceURI) || (vs.find((v) => /^en/i.test(v.lang))?.voiceURI) || (vs[0]?.voiceURI || ""));
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { try { window.speechSynthesis.cancel(); } catch { /* */ } window.speechSynthesis.onvoiceschanged = null; };
  }, [supportsTTS]);

  // Build sentences with DOM offsets so we can highlight each one as it's read.
  const collectSentences = () => {
    const root = ref.current;
    if (!root) return [];
    const nodes = [];
    let full = "";
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) { nodes.push({ node: n, start: full.length, text: n.nodeValue }); full += n.nodeValue; }
    const out = [];
    const re = /[^.!?…]*[.!?…]+["'”’)\]]?|\S[^.!?…]*$/g;
    let m;
    while ((m = re.exec(full))) {
      const text = m[0].trim();
      if (text) out.push({ text, start: m.index, end: m.index + m[0].length, nodes });
    }
    return out;
  };
  const rangeFor = (nodes, start, end) => {
    let sN, sO, eN, eO;
    for (const it of nodes) {
      const nEnd = it.start + it.text.length;
      if (sN == null && start >= it.start && start <= nEnd) { sN = it.node; sO = start - it.start; }
      if (end >= it.start && end <= nEnd) { eN = it.node; eO = end - it.start; break; }
    }
    if (!sN || !eN) return null;
    const r = document.createRange();
    try { r.setStart(sN, sO); r.setEnd(eN, eO); } catch { return null; }
    return r;
  };
  const highlightSentence = (s) => {
    const r = rangeFor(s.nodes, s.start, s.end);
    if (!r) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
    const rect = r.getBoundingClientRect();
    const cont = scrollRef.current;
    if (cont && rect) {
      const cr = cont.getBoundingClientRect();
      if (rect.top < cr.top + 80 || rect.bottom > cr.bottom - 80) {
        cont.scrollTop += rect.top - cr.top - cont.clientHeight / 2;
      }
    }
  };
  const speakFrom = (startIdx) => {
    const st = speechState.current;
    st.stop = false;
    const voice = voices.find((v) => v.voiceURI === voiceURI);
    const next = (i) => {
      if (st.stop || i >= st.sentences.length) { setReading(false); setPaused(false); window.getSelection()?.removeAllRanges(); return; }
      st.idx = i;
      const s = st.sentences[i];
      highlightSentence(s);
      const u = new SpeechSynthesisUtterance(s.text);
      u.rate = rate; if (voice) { u.voice = voice; u.lang = voice.lang; }
      u.onend = () => { if (!st.stop) next(i + 1); };
      u.onerror = () => { if (!st.stop) next(i + 1); };
      window.speechSynthesis.speak(u);
    };
    next(startIdx);
  };
  const startReading = (fromCursor) => {
    if (!supportsTTS) return;
    window.speechSynthesis.cancel();
    const sentences = collectSentences();
    if (!sentences.length) return;
    speechState.current.sentences = sentences;
    let startIdx = 0;
    if (fromCursor) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
        // map caret to a global offset
        let off = 0; const r = sel.getRangeAt(0);
        for (const it of sentences[0].nodes) {
          if (it.node === r.startContainer) { off = it.start + r.startOffset; break; }
        }
        const found = sentences.findIndex((s) => off >= s.start && off < s.end);
        if (found > 0) startIdx = found;
      }
    }
    ref.current?.blur();
    setReading(true); setPaused(false);
    speakFrom(startIdx);
  };
  const pauseResume = () => {
    if (paused) { window.speechSynthesis.resume(); setPaused(false); }
    else { window.speechSynthesis.pause(); setPaused(true); }
  };
  const stopReading = () => {
    speechState.current.stop = true;
    window.speechSynthesis.cancel();
    setReading(false); setPaused(false);
    window.getSelection()?.removeAllRanges();
  };


  useEffect(() => {
    if (ref.current) ref.current.innerHTML = initialHtml || "<p></p>";
    updateStats();
    // Restore the scroll position we had before leaving this chapter's manuscript.
    if (scrollRef.current && initialScroll > 0) {
      requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = initialScroll; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSel = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  useImperativeHandle(apiRef, () => ({
    getScroll() { return scrollRef.current?.scrollTop || 0; },
    setScroll(top) { if (scrollRef.current) scrollRef.current.scrollTop = top || 0; },
    isFocused() { return !!(ref.current && document.activeElement === ref.current); },
    setHtml(html) {
      if (!ref.current) return;
      ref.current.innerHTML = html || "<p></p>";
      updateStats();
    },
    appendHtml(html) {
      if (!ref.current || !html) return;
      const cur = ref.current.innerHTML.trim();
      const empty = !cur || cur === "<p></p>" || cur === "<br>" || cur === "<p><br></p>";
      ref.current.innerHTML = empty ? html : cur + html;
      updateStats();
      onChange?.(ref.current.innerHTML);
    },
    insertText(text) {
      if (!ref.current) return;
      ref.current.focus();
      const sel = window.getSelection();
      let range = savedRange.current;
      if (!range || !ref.current.contains(range.startContainer)) {
        range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(range);
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
      savedRange.current = range.cloneRange();
      handleInput();
    },
    replaceText(original, suggestion) {
      if (!ref.current || !original) return false;
      const walker = document.createTreeWalker(ref.current, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const idx = node.nodeValue.indexOf(original);
        if (idx !== -1) {
          node.nodeValue = node.nodeValue.slice(0, idx) + suggestion + node.nodeValue.slice(idx + original.length);
          handleInput();
          return true;
        }
      }
      return false;
    },
  }));

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    handleInput();
    setTick((t) => t + 1);
  };

  const updateStats = useCallback(() => {
    const text = (ref.current?.innerText || "").trim();
    const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
    onStats?.({ words, minutes: Math.max(1, Math.round(words / 200)) });
  }, [onStats]);

  const handleInput = (e) => {
    if (autocorrect && !correctingRef.current && e && e.nativeEvent) {
      try { maybeAutocorrect(e.nativeEvent); } catch { /* never block typing */ }
    }
    onChange?.(ref.current?.innerHTML || "");
    updateStats();
  };

  // Undoable in-place replacement of text[start,end) within a text node, keeping
  // the caret after the trailing boundary char(s) the user just typed.
  const applyReplacement = (node, start, end, replacement, caretOffset) => {
    const tailLen = caretOffset - end; // boundary char(s) between word end and caret
    const r = document.createRange();
    r.setStart(node, start); r.setEnd(node, end);
    const sel = window.getSelection();
    sel.removeAllRanges(); sel.addRange(r);
    correctingRef.current = true;
    document.execCommand("insertText", false, replacement);
    correctingRef.current = false;
    try {
      const after = window.getSelection().getRangeAt(0);
      const cNode = after.startContainer;
      const want = Math.min(after.startOffset + tailLen, (cNode.nodeValue || "").length);
      const nr = document.createRange();
      nr.setStart(cNode, want); nr.collapse(true);
      sel.removeAllRanges(); sel.addRange(nr);
      savedRange.current = nr.cloneRange();
    } catch { /* caret best-effort */ }
  };

  const maybeAutocorrect = (ne) => {
    const it = ne.inputType;
    const data = ne.data;
    const isSpace = it === "insertText" && data === " ";
    const isPunct = it === "insertText" && !!data && /^[.,!?;:)\]"'’”]$/.test(data);
    if (!isSpace && !isPunct) return; // fire on word boundaries only
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    const caret = range.startOffset;
    if (node.nodeType !== 3 || !ref.current || !ref.current.contains(node)) return;
    const text = node.nodeValue || "";
    const boundaryPos = caret - 1;
    if (boundaryPos < 0) return;

    // Collapse an accidental double space into one.
    if (isSpace && boundaryPos - 1 >= 0 && text[boundaryPos - 1] === " ") {
      applyReplacement(node, boundaryPos - 1, caret, " ", caret);
      return;
    }

    // Extract the word immediately before the boundary char.
    let start = boundaryPos;
    while (start > 0 && /[A-Za-z]/.test(text[start - 1])) start--;
    const word = text.slice(start, boundaryPos);
    if (!word) return;
    const lw = word.toLowerCase();

    // Personal rules always win (the user explicitly taught these).
    const custom = customRules && customRules[lw];
    let base;
    if (custom !== undefined) {
      base = matchCase(word, custom);
    } else {
      if (protectedWords && protectedWords.has(lw)) return; // never touch Codex names
      base = correctToken(word) ?? word;
    }

    // Is this the first word of a sentence? (for auto-capitalisation)
    let i = start - 1;
    while (i >= 0 && /\s/.test(text[i])) i--;
    const sentenceStart = i < 0 || /[.!?]/.test(text[i]);
    if (sentenceStart && /^[a-z]/.test(base)) base = base.charAt(0).toUpperCase() + base.slice(1);
    if (base === word) return;
    applyReplacement(node, start, boundaryPos, base, caret);
  };

  const handleDrop = (e) => {
    const text = e.dataTransfer.getData("text/plain");
    if (!text) return; // let the browser handle non-text drops
    e.preventDefault();
    let range = null;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset); range.collapse(true); }
    }
    if (!range || !ref.current.contains(range.startContainer)) {
      range = document.createRange();
      range.selectNodeContents(ref.current);
      range.collapse(false);
    }
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    savedRange.current = range.cloneRange();
    handleInput();
  };

  const setHeading = (tag) => exec("formatBlock", tag);
  const isActive = (cmd) => { try { return document.queryCommandState(cmd); } catch { return false; } };

  const insertSceneBreak = () => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, "<hr><p><br></p>");
    handleInput();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    const toInsert = html
      ? sanitizePastedHtml(html)
      : escapeHtml(text).replace(/\n/g, "<br>");
    document.execCommand("insertHTML", false, toInsert);
    handleInput();
  };

  const addLink = () => {
    let url = window.prompt("Link URL (e.g. https://...)");
    if (!url) return;
    url = url.trim();
    if (!/^(https?:|mailto:)/i.test(url)) url = "https://" + url;
    ref.current?.focus();
    const sel = window.getSelection();
    const hasText = sel && !sel.isCollapsed && sel.toString().trim();
    if (hasText) {
      document.execCommand("createLink", false, url);
    } else {
      const text = (window.prompt("Link text", url) || url).trim() || url;
      const safeUrl = url.replace(/"/g, "&quot;");
      document.execCommand("insertHTML", false, `<a href="${safeUrl}">${escapeHtml(text)}</a>`);
    }
    handleInput();
  };

  return (
    <div className="flex flex-col h-full">
      <div data-testid="rich-text-editor-toolbar"
        className="sticky top-0 z-10 flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-border bg-background/90 backdrop-blur-md">
        {projectId && aiTools && (
          <>
            <button onClick={() => setSparkOpen(true)} title="Spark a chapter with AI" data-testid="toolbar-spark"
              className="h-8 px-2 rounded-md flex items-center gap-1 text-sm text-primary hover:bg-primary/10 font-medium">
              <SparkIcon className="w-4 h-4" /> Spark
            </button>
            <span className="w-px h-5 bg-border mx-1" />
            <SparkDialog
              open={sparkOpen} onOpenChange={setSparkOpen}
              projectId={projectId} chapterId={chapterId} prevChapterId={prevChapterId}
              onInsert={(html) => {
                if (!ref.current || !html) return;
                const cur = ref.current.innerHTML.trim();
                const empty = !cur || cur === "<p></p>" || cur === "<br>" || cur === "<p><br></p>";
                ref.current.innerHTML = empty ? html : cur + html;
                updateStats();
                onChange?.(ref.current.innerHTML);
              }}
            />
          </>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 px-2 rounded-md hover:bg-secondary flex items-center gap-1 text-sm" title="Paragraph style" data-testid="toolbar-heading">
              <Type className="w-4 h-4" /> Style
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover">
            <DropdownMenuItem onClick={() => setHeading("h1")}><Heading1 className="w-4 h-4 mr-2" /> Heading 1</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHeading("h2")}><Heading2 className="w-4 h-4 mr-2" /> Heading 2</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHeading("h3")}><Heading3 className="w-4 h-4 mr-2" /> Heading 3</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHeading("p")}><Pilcrow className="w-4 h-4 mr-2" /> Paragraph</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHeading("blockquote")}><Quote className="w-4 h-4 mr-2" /> Quote</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 px-2 rounded-md hover:bg-secondary flex items-center gap-1 text-sm" title="Font" data-testid="toolbar-font">
              Font
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover">
            {FONTS.map((f) => (
              <DropdownMenuItem key={f.value} onClick={() => exec("fontName", f.value)} style={{ fontFamily: f.value }}>
                {f.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Sep />
        <Btn testid="toolbar-bold" title="Bold" active={isActive("bold")} onClick={() => exec("bold")}><Bold className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-italic" title="Italic" active={isActive("italic")} onClick={() => exec("italic")}><Italic className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-underline" title="Underline" active={isActive("underline")} onClick={() => exec("underline")}><Underline className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-strike" title="Strikethrough" active={isActive("strikeThrough")} onClick={() => exec("strikeThrough")}><Strikethrough className="w-4 h-4" /></Btn>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-8 h-8 rounded-md hover:bg-secondary flex items-center justify-center" title="Text colour" data-testid="toolbar-color">
              <span className="w-4 h-4 rounded-full border border-border" style={{ background: "linear-gradient(135deg,#ef4444,#8b5cf6)" }} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover">
            <div className="grid grid-cols-3 gap-1.5 p-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => exec("foreColor", c)} className="w-7 h-7 rounded-md border border-border" style={{ background: c }} />
              ))}
              <button onClick={() => exec("foreColor", "inherit")} className="w-7 h-7 rounded-md border border-border flex items-center justify-center text-xs col-span-3">Reset</button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <Sep />
        <Btn testid="toolbar-ul" title="Bullet list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-ol" title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-link" title="Insert link" onClick={addLink}><Link2 className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-scene-break" title="Scene break (time skip)" onClick={insertSceneBreak}><Minus className="w-4 h-4" /></Btn>

        <Sep />
        <Btn testid="toolbar-align-left" title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-align-center" title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-align-right" title="Align right" onClick={() => exec("justifyRight")}><AlignRight className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-align-justify" title="Justify" onClick={() => exec("justifyFull")}><AlignJustify className="w-4 h-4" /></Btn>

        <Sep />
        <Btn testid="toolbar-undo" title="Undo" onClick={() => exec("undo")}><Undo className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-redo" title="Redo" onClick={() => exec("redo")}><Redo className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-clear" title="Clear formatting" onClick={() => exec("removeFormat")}><RemoveFormatting className="w-4 h-4" /></Btn>
        <Btn testid="toolbar-autocorrect" active={autocorrect}
          title={autocorrect ? "Autocorrect is on (fixes common typos as you type)" : "Autocorrect is off"}
          onClick={() => setAutocorrect((v) => !v)}><Wand2 className="w-4 h-4" /></Btn>

        {supportsTTS && (
          <>
            <Sep />
            {!reading ? (
              <button type="button" data-testid="read-aloud-button" title="Read this chapter aloud (from your cursor if placed)"
                onMouseDown={(e) => { e.preventDefault(); startReading(true); }}
                className="h-8 px-2 rounded-md hover:bg-secondary flex items-center gap-1.5 text-sm shrink-0 text-foreground">
                <Volume2 className="w-4 h-4" /> <span className="hidden md:inline">Read aloud</span>
              </button>
            ) : (
              <div className="flex items-center gap-0.5 shrink-0">
                <Btn testid="read-aloud-pause" title={paused ? "Resume" : "Pause"} onClick={pauseResume}>
                  {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </Btn>
                <Btn testid="read-aloud-stop" title="Stop reading" onClick={stopReading}><Square className="w-4 h-4" /></Btn>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="read-aloud-settings" title="Voice & speed"
                  className="h-8 px-1.5 rounded-md hover:bg-secondary flex items-center text-xs shrink-0 text-muted-foreground">
                  {rate.toFixed(1)}×
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover w-60 p-2">
                <div className="px-1 pb-2">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Speed</div>
                  <input type="range" min="0.6" max="1.6" step="0.1" value={rate} data-testid="read-aloud-rate"
                    onChange={(e) => { const r = parseFloat(e.target.value); setRate(r); if (reading) { stopReading(); setTimeout(() => startReading(false), 50); } }}
                    className="w-full accent-primary" />
                  <div className="text-xs text-muted-foreground text-right">{rate.toFixed(1)}×</div>
                </div>
                <div className="px-1">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Voice</div>
                  <select value={voiceURI} data-testid="read-aloud-voice"
                    onChange={(e) => setVoiceURI(e.target.value)}
                    className="w-full text-sm bg-secondary rounded-md px-2 py-1.5 border border-border">
                    {voices.map((v) => <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>)}
                  </select>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        <Sep />
        <div className="flex items-center gap-0.5 shrink-0" title="Text size (display only — doesn't change the saved document)">
          <button type="button" data-testid="toolbar-text-smaller"
            onMouseDown={(e) => { e.preventDefault(); stepScale(-1); }}
            disabled={scale <= SCALES[0]}
            className="w-8 h-8 rounded-md hover:bg-secondary flex items-center justify-center shrink-0 text-foreground disabled:opacity-40">
            <span className="text-xs font-semibold leading-none">A</span>
          </button>
          <button type="button" data-testid="toolbar-text-size-reset"
            onMouseDown={(e) => { e.preventDefault(); setScale(1); }}
            title="Reset text size to 100%"
            className="h-8 px-1 rounded-md hover:bg-secondary text-[11px] tabular-nums text-muted-foreground shrink-0 min-w-[2.6rem]">
            {Math.round(scale * 100)}%
          </button>
          <button type="button" data-testid="toolbar-text-larger"
            onMouseDown={(e) => { e.preventDefault(); stepScale(1); }}
            disabled={scale >= SCALES[SCALES.length - 1]}
            className="w-8 h-8 rounded-md hover:bg-secondary flex items-center justify-center shrink-0 text-foreground disabled:opacity-40">
            <span className="text-lg font-semibold leading-none">A</span>
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-10 lg:py-16 px-4">
        <div className="manuscript-page mx-auto">
          <div ref={ref} data-testid="editor-content-area" contentEditable suppressContentEditableWarning
            spellCheck={spellcheck} onInput={handleInput}
            onKeyUp={saveSel} onMouseUp={saveSel} onBlur={saveSel}
            onDrop={handleDrop}
            onPaste={handlePaste}
            style={{ fontSize: `${(BASE_REM * scale).toFixed(4)}rem` }}
            data-placeholder="Begin your story here..."
            className="manuscript min-h-[60vh] text-foreground" />
        </div>
      </div>
    </div>
  );
});
